import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { Client, VoiceAgentPrompt } from '../../types';
import { Phone, Mail, User, Loader2 } from 'lucide-react';
interface VoiceAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  onStartCall: (clientId: string, promptId: string, customPrompt?: string) => void;
}
const promptLibrary: VoiceAgentPrompt[] = [{
  id: 'p1',
  name: 'Terminvereinbarung',
  description: 'Vereinbaren Sie einen Termin für eine Beratung zur IT-Infrastruktur.',
  promptText: 'Guten Tag! Ich rufe von neocube an. Wir möchten gerne einen Termin für eine Beratung zu Ihrer IT-Infrastruktur vereinbaren. Wann würde es Ihnen passen?',
  category: 'sales'
}, {
  id: 'p2',
  name: 'Zufriedenheitsumfrage',
  description: 'Führen Sie eine kurze Zufriedenheitsumfrage durch und erfragen Sie Verbesserungswünsche.',
  promptText: 'Guten Tag! Wir möchten kurz Ihre Zufriedenheit mit unseren Services erfragen. Wie zufrieden sind Sie auf einer Skala von 1-10? Gibt es Verbesserungswünsche?',
  category: 'survey'
}, {
  id: 'p3',
  name: 'Produktinteresse',
  description: 'Ermitteln Sie Interesse an neuen Managed Services und Cloud-Lösungen.',
  promptText: 'Guten Tag! Wir haben neue Managed Services und Cloud-Lösungen im Angebot. Hätten Sie Interesse an weiteren Informationen dazu?',
  category: 'sales'
}, {
  id: 'p4',
  name: 'Vertragsverlängerung',
  description: 'Besprechen Sie die anstehende Vertragsverlängerung und klären Sie offene Fragen.',
  promptText: 'Guten Tag! Ihr Vertrag läuft bald aus. Ich möchte mit Ihnen die Verlängerung besprechen und offene Fragen klären.',
  category: 'sales'
}, {
  id: 'p5',
  name: 'Update-Erinnerung',
  description: 'Erinnern Sie an ausstehende System-Updates und vereinbaren Sie einen Wartungstermin.',
  promptText: 'Guten Tag! Wir haben festgestellt, dass bei Ihnen noch wichtige System-Updates ausstehen. Können wir einen Wartungstermin vereinbaren?',
  category: 'support'
}, {
  id: 'p6',
  name: 'Feedback zu Service',
  description: 'Holen Sie Feedback zum letzten Service-Einsatz ein.',
  promptText: 'Guten Tag! Wir möchten gerne Feedback zu unserem letzten Service-Einsatz bei Ihnen einholen. Wie war Ihre Erfahrung?',
  category: 'survey'
}, {
  id: 'custom',
  name: 'Benutzerdefiniert',
  description: 'Erstellen Sie einen individuellen Prompt für diesen Anruf.',
  promptText: '',
  category: 'custom'
}];
export function VoiceAgentModal({
  isOpen,
  onClose,
  client,
  onStartCall
}: VoiceAgentModalProps) {
  const [selectedPromptId, setSelectedPromptId] = useState('p1');
  const [customPrompt, setCustomPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const selectedPrompt = promptLibrary.find(p => p.id === selectedPromptId);
  const handleStartCall = async () => {
    if (!client) return;
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    onStartCall(client.id, selectedPromptId, selectedPromptId === 'custom' ? customPrompt : undefined);
    setIsLoading(false);
    onClose();
  };
  if (!client) return null;
  return <Modal isOpen={isOpen} onClose={onClose} title="AI Voice Agent - Anruf konfigurieren" data-id="element-140">
      <div className="space-y-6" data-id="element-141">
        {/* Customer Info */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-142">
          <h3 className="text-sm font-semibold text-slate-700 mb-3" data-id="element-143">
            Kundeninformationen
          </h3>
          <div className="space-y-2" data-id="element-144">
            <div className="flex items-center gap-2 text-sm" data-id="element-145">
              <User className="h-4 w-4 text-slate-400" data-id="element-146" />
              <span className="font-medium text-slate-900" data-id="element-147">{client.name}</span>
              <span className="text-slate-500" data-id="element-148">({client.contactPerson})</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-149">
              <Phone className="h-4 w-4 text-slate-400" data-id="element-150" />
              <span data-id="element-151">{client.phone}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-152">
              <Mail className="h-4 w-4 text-slate-400" data-id="element-153" />
              <span data-id="element-154">{client.email}</span>
            </div>
          </div>
        </div>

        {/* Prompt Selection */}
        <div data-id="element-155">
          <Select label="Prompt auswählen" options={promptLibrary.map(p => ({
          value: p.id,
          label: p.name
        }))} value={selectedPromptId} onChange={e => setSelectedPromptId(e.target.value)} data-id="element-156" />

          {selectedPrompt && <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-157">
              <p className="text-sm text-blue-900" data-id="element-158">
                {selectedPrompt.description}
              </p>
            </div>}
        </div>

        {/* Custom Prompt */}
        {selectedPromptId === 'custom' && <div data-id="element-159">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-160">
              Benutzerdefinierter Prompt
            </label>
            <textarea value={customPrompt} onChange={e => setCustomPrompt(e.target.value)} placeholder="Geben Sie hier Ihren individuellen Gesprächsleitfaden ein..." className="w-full h-32 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none" data-id="element-161" />
          </div>}

        {/* Prompt Preview */}
        {selectedPromptId !== 'custom' && selectedPrompt && <div data-id="element-162">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-163">
              Gesprächsleitfaden
            </label>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 leading-relaxed" data-id="element-164">
              {selectedPrompt.promptText}
            </div>
          </div>}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-4 border-t border-slate-200" data-id="element-165">
          <Button variant="secondary" onClick={onClose} disabled={isLoading} data-id="element-166">
            Abbrechen
          </Button>
          <Button onClick={handleStartCall} disabled={isLoading || selectedPromptId === 'custom' && !customPrompt.trim()} leftIcon={isLoading ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-168" /> : <Phone className="h-4 w-4" data-id="element-169" />} data-id="element-167">
            {isLoading ? 'Wird gestartet...' : 'Anruf starten'}
          </Button>
        </div>
      </div>
    </Modal>;
}